import { apiGet } from '@/lib/api';
import { pricebookKeys } from '@/lib/queryKeys';
import { useApiQuery } from '../useApiQuery';
import { usePricebookApi } from './usePricebookApi';
import type {
  PricebookListParams,
  PricebookListResponse,
  PricebookItem,
  ServiceComponent,
  MembershipConfig,
  UpsellRule,
  UpsellItem,
  UpsellRelationshipType,
  PricebookTag,
  ItemCategoryMembership,
} from '@/types/pricebook';
import {
  ITEM_TYPE_TO_BACKEND,
  backendTypeToFrontend,
  normalizeDisplayConfig,
  parsePriceStrategy,
} from '@/types/pricebook';

const UPSELL_RELATIONSHIP_TYPES: UpsellRelationshipType[] = ['upsell', 'accessory', 'alternative'];

function normalizeUpsellItems(value: unknown): UpsellItem[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((entry) => {
    const u = (entry ?? {}) as Record<string, unknown>;
    const c = (u.conditions ?? {}) as Record<string, unknown>;
    const rel = u.relationshipType as UpsellRelationshipType;
    return {
      itemId: String(u.itemId ?? ''),
      reason: u.reason != null ? String(u.reason) : null,
      auto: u.auto === true,
      aiWeight: u.aiWeight != null ? Number(u.aiWeight) : 0.5,
      relationshipType: UPSELL_RELATIONSHIP_TYPES.includes(rel) ? rel : 'upsell',
      conditions: {
        assetAgeGt: c.assetAgeGt != null ? Number(c.assetAgeGt) : null,
        assetCategory: c.assetCategory != null ? String(c.assetCategory) : null,
        estimateTotalGt: c.estimateTotalGt != null ? Number(c.estimateTotalGt) : null,
        membershipStatus: c.membershipStatus != null ? String(c.membershipStatus) : null,
        requiresItems: Array.isArray(c.requiresItems) ? (c.requiresItems as string[]) : [],
      },
    };
  });
}

interface UsePricebookItemsOptions {
  enabled?: boolean;
  showErrorToast?: boolean;
  /** Fetch every page, for catalog-wide decisions such as automatic offers. */
  allPages?: boolean;
}

export function usePricebookItems(
  params: PricebookListParams = {},
  options?: UsePricebookItemsOptions,
) {
  const { client, ep } = usePricebookApi();
  const allPages = options?.allPages === true;

  return useApiQuery<PricebookListResponse>({
    queryKey: pricebookKeys.list({ ...params, allPages }),
    enabled: options?.enabled,
    showErrorToast: options?.showErrorToast,
    queryFn: async ({ signal }) => {
      const { search, type, tagIds, ids, includeSubcategories, includeArchived, ...rest } = params;
      const pageSize = params.limit ?? 100;
      const firstPage = params.page ?? 1;
      const fetchPage = async (page?: number) => {
        const apiParams: Record<string, unknown> = {
          ...rest,
          ...(allPages ? { page: page ?? firstPage, limit: pageSize } : {}),
        };
        if (search) apiParams.q = search;
        // Only sent when set, for the same reason as `includeSubcategories`.
        if (includeArchived) apiParams.includeArchived = 'true';
        // Only sent when explicitly disabled: the server already includes
        // subfolders by default, and spreading `undefined` through would put a
        // literal "undefined" on the query string.
        if (includeSubcategories === false) apiParams.includeSubcategories = 'false';
        if (type) apiParams.type = ITEM_TYPE_TO_BACKEND[type] ?? type.toUpperCase();
        if (tagIds && tagIds.length > 0) apiParams.tagIds = tagIds.join(',');
        if (ids && ids.length > 0) apiParams.ids = ids.join(',');
        return apiGet<Record<string, unknown>>(client, ep.list(), {
          params: apiParams,
          signal,
        });
      };

      const first = await fetchPage(allPages ? firstPage : undefined);
      const rawItems = (first.data ?? first.items ?? []) as Array<Record<string, unknown>>;
      const total = Number(first.total ?? rawItems.length);
      if (allPages) {
        let nextPage = firstPage + 1;
        while (rawItems.length < total) {
          const response = await fetchPage(nextPage);
          const nextItems = (response.data ?? response.items ?? []) as Array<
            Record<string, unknown>
          >;
          if (nextItems.length === 0) break;
          rawItems.push(...nextItems);
          nextPage += 1;
        }
      }
      return {
        items: rawItems.map(mapOperationsLineItem),
        total,
        page: Number(first.page ?? firstPage),
        limit: Number(first.limit ?? pageSize),
      };
    },
  });
}

/** Maps the backend PricebookLineItem shape to the frontend PricebookItem type. */
export function mapOperationsLineItem(raw: Record<string, unknown>): PricebookItem {
  return {
    // Identity
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    description: raw.description ? String(raw.description) : undefined,
    type: backendTypeToFrontend(String(raw.type ?? 'SERVICE')),
    sku: raw.code != null ? String(raw.code) : undefined,
    status: raw.status != null ? (String(raw.status) as PricebookItem['status']) : undefined,
    businessUnitIds: Array.isArray(raw.businessUnitIds) ? raw.businessUnitIds.map(String) : [],

    // Pricing
    price: Number(raw.basePrice ?? 0),
    priceType: raw.priceStrategy === 'DYNAMIC' ? 'hourly' : 'flat',
    priceStrategy: parsePriceStrategy(raw.priceStrategy) ?? 'FLAT',
    // Kept alongside the narrowed value, never instead of it: the `?? 'FLAT'`
    // above is a display default, and callers that must not restate it as fact
    // (the export) read this instead. See PricebookItem.priceStrategyRaw.
    priceStrategyRaw: raw.priceStrategy != null ? String(raw.priceStrategy) : undefined,
    laborRate: raw.laborRate != null ? Number(raw.laborRate) : undefined,
    // COST, not price — carried for BOTH strategies. See PricebookItem.
    laborCostRate: raw.laborCostRate != null ? Number(raw.laborCostRate) : undefined,
    estimatedHours: raw.estimatedHours != null ? Number(raw.estimatedHours) : undefined,
    laborCostLines: Array.isArray(raw.laborCostLines)
      ? (raw.laborCostLines as PricebookItem['laborCostLines'])
      : undefined,
    dynamicPricingConfig:
      raw.dynamicPricingConfig && typeof raw.dynamicPricingConfig === 'object'
        ? (raw.dynamicPricingConfig as PricebookItem['dynamicPricingConfig'])
        : undefined,
    cost: raw.costBasis != null ? Number(raw.costBasis) : undefined,
    markupPercent: raw.markupPercent != null ? Number(raw.markupPercent) : undefined,
    memberPrice: raw.memberPrice != null ? Number(raw.memberPrice) : null,
    memberDiscountPercent:
      raw.memberDiscountPercent != null ? Number(raw.memberDiscountPercent) : null,
    currency: raw.currency != null ? String(raw.currency) : undefined,
    unit: raw.unit ? String(raw.unit) : undefined,

    // Tax
    taxable: raw.taxable === true,
    taxCategory: raw.taxCategory != null ? String(raw.taxCategory) : null,
    taxCode: raw.taxCode != null ? String(raw.taxCode) : null,
    taxLineType:
      raw.taxLineType != null ? (String(raw.taxLineType) as PricebookItem['taxLineType']) : null,

    // GL / Display
    glAccount: raw.glAccount != null ? String(raw.glAccount) : null,
    glClass: raw.glClass != null ? String(raw.glClass) : null,
    glCogs: raw.glCogs != null ? String(raw.glCogs) : null,
    glExpenseAccount: raw.glExpenseAccount != null ? String(raw.glExpenseAccount) : null,
    displayOrder: raw.displayOrder != null ? Number(raw.displayOrder) : undefined,
    displayConfig: normalizeDisplayConfig(raw.displayConfig),

    // NOT-590 — the materials a SERVICE includes, and how the customer sees
    // them. The list itself is fetched on demand; the list row only needs the
    // display mode.
    components: Array.isArray(raw.components) ? (raw.components as ServiceComponent[]) : undefined,
    partCostSources: Array.isArray(raw.partCostSources)
      ? (raw.partCostSources as PricebookItem['partCostSources'])
      : undefined,
    componentDisplayMode:
      raw.componentDisplayMode === 'ROLLED_UP' || raw.componentDisplayMode === 'ITEMIZED'
        ? raw.componentDisplayMode
        : undefined,

    // Membership
    membershipConfig:
      raw.membershipConfig != null ? (raw.membershipConfig as MembershipConfig) : undefined,
    membershipPlanIds: Array.isArray(raw.membershipPlanIds)
      ? (raw.membershipPlanIds as string[])
      : undefined,

    // Upsell
    upsellRules: Array.isArray(raw.upsellRules) ? (raw.upsellRules as UpsellRule[]) : undefined,
    upsellItems: normalizeUpsellItems(raw.upsellItems),

    // AI & Search
    aiTags: Array.isArray(raw.aiTags) ? (raw.aiTags as string[]) : undefined,
    requiredAssets: Array.isArray(raw.requiredAssets)
      ? (raw.requiredAssets as string[])
      : undefined,
    hazardLevel: raw.hazardLevel != null ? Number(raw.hazardLevel) : undefined,
    retrievalBoost: raw.retrievalBoost != null ? Number(raw.retrievalBoost) : undefined,
    equipmentDescriptionId:
      raw.equipmentDescriptionId != null ? String(raw.equipmentDescriptionId) : null,
    isConfigurable: raw.isConfigurable === true,
    configurations: Array.isArray(raw.configurations)
      ? (raw.configurations as PricebookItem['configurations'])
      : [],
    itemVersion: raw.itemVersion != null ? Number(raw.itemVersion) : undefined,

    // Image & tags
    imageUrl: raw.imageUrl != null ? String(raw.imageUrl) : null,
    imagePreviewUrl: raw.imagePreviewUrl != null ? String(raw.imagePreviewUrl) : null,
    tags: Array.isArray(raw.tags) ? (raw.tags as PricebookTag[]) : undefined,
    categories: Array.isArray(raw.categories)
      ? (raw.categories as ItemCategoryMembership[])
      : undefined,

    // Timestamps
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
  };
}
